import { formatISO } from 'date-fns';
import { TransformedBooking } from '../types';
import { CoverageStrength, CoverageSummary } from '../types/coverage';
import {
  ProviderMatchBatchRequest,
  ProviderMatchBatchResponseItem,
  ProviderMatchResult,
} from '@/features/users/providers/api/matches';

export const coveragePillMeta: Record<
  CoverageStrength,
  { label: string; color: 'success' | 'warning' | 'error' }
> = {
  direct: { label: 'Coverage: strong', color: 'success' },
  nearby: { label: 'Coverage: nearby only', color: 'warning' },
  none: { label: 'Coverage: none', color: 'error' },
};

export const coverageMessageByStrength: Record<CoverageStrength, string> = {
  direct: 'Cleaners already cover this postcode.',
  nearby:
    'Only nearby cleaners cover this postcode. Confirm travel time before assigning.',
  none: 'No cleaners matched this postcode yet. Consider expanding coverage.',
};

export const extractOutwardCode = (postcode?: string): string | null => {
  if (!postcode) return null;
  const trimmed = postcode.trim();
  if (!trimmed) return null;
  const [outward] = trimmed.toUpperCase().split(/\s+/);
  return outward || null;
};

export const buildMatchRequestPayload = (
  booking: TransformedBooking,
  options?: { limit?: number }
): ProviderMatchBatchRequest | null => {
  const outwardCode = extractOutwardCode(booking.address?.postcode);
  const serviceType = booking.serviceTypeId
    ? String(booking.serviceTypeId)
    : '';

  if (!outwardCode || !serviceType) {
    return null;
  }

  const bookingStart =
    booking.start instanceof Date ? booking.start : new Date(booking.start);
  const bookingEndRaw =
    booking.end instanceof Date ? booking.end : new Date(booking.end);
  const bookingEnd = Number.isNaN(bookingEndRaw.getTime())
    ? new Date(
        bookingStart.getTime() + (booking.totalHours ?? 0) * 60 * 60 * 1000
      )
    : bookingEndRaw;

  return {
    reference: String(booking.id),
    outward_code: outwardCode,
    service_type: serviceType,
    date: formatISO(bookingStart),
    start_time: formatISO(bookingStart),
    end_time: formatISO(bookingEnd),
    limit: options?.limit ?? 5,
    address_id: booking.address?.id,
    client_user_id: booking.clientUserId,
  };
};

export const buildCoverageSummary = (
  result?: Pick<ProviderMatchBatchResponseItem, 'matches' | 'reason'>
): CoverageSummary | null => {
  if (!result) return null;
  const matches = filterAvailableMatches(result.matches);

  if (!matches.length) {
    return {
      coverage: 'none',
      matchesCount: 0,
      message: result.reason || coverageMessageByStrength.none,
      reason: result.reason,
    };
  }

  const hasDirect = matches.some((match) => match.match_type === 'direct');
  const coverage: CoverageStrength = hasDirect ? 'direct' : 'nearby';

  return {
    coverage,
    matchesCount: matches.length,
    message: coverageMessageByStrength[coverage],
    reason: result.reason,
  };
};

export const buildMissingMatchReason = (
  booking: TransformedBooking
): string => {
  const missing: string[] = [];
  if (!extractOutwardCode(booking.address?.postcode)) {
    missing.push('postcode');
  }
  if (!booking.serviceTypeId) {
    missing.push('service type');
  }

  if (!missing.length) {
    return 'Missing booking details for cleaner suggestions.';
  }

  const label = missing.join(' and ');
  return `Add a ${label} to see cleaner suggestions.`;
};

export const getMatchLabel = (matchesCount: number): string =>
  `${matchesCount} match${matchesCount === 1 ? '' : 'es'}`;

export const extractMatchEntryByReference = (
  entries: ProviderMatchBatchResponseItem[]
): Record<number, ProviderMatchBatchResponseItem> => {
  return entries.reduce<Record<number, ProviderMatchBatchResponseItem>>(
    (acc, entry) => {
      const ref = entry.request?.reference;
      if (!ref) return acc;
      const id = Number(ref);
      if (Number.isNaN(id)) return acc;
      acc[id] = entry;
      return acc;
    },
    {}
  );
};

export const filterAvailableMatches = (
  matches?: ProviderMatchResult[]
): ProviderMatchResult[] =>
  (matches ?? []).filter(
    (match) =>
      Boolean(match.availability?.available_on_date) &&
      !match.availability?.conflicting_session
  );
